import { Plugin, type Runtime } from "@squide/core";
import { EnvironmentVariablesRegistry, type EnvironmentVariableKey, type EnvironmentVariables } from "./EnvironmentVariablesRegistry.ts";

export const EnvironmentVariablesPluginName = "env-vars-plugin";

export interface EnvironmentVariablesPluginOptions {
    variables?: Partial<EnvironmentVariables>;
}

export class EnvironmentVariablesPlugin extends Plugin {
    readonly #environmentVariablesRegistry = new EnvironmentVariablesRegistry();

    constructor(runtime: Runtime, options: EnvironmentVariablesPluginOptions = {}) {
        super(EnvironmentVariablesPluginName, runtime);

        const {
            variables
        } = options;

        if (variables) {
            this.#environmentVariablesRegistry.addVariables(variables);
        }
    }

    registerVariable<T extends EnvironmentVariableKey>(key: T, value: EnvironmentVariables[T]) {
        this.#environmentVariablesRegistry.add(key, value);

        this._runtime.logger.debug(`[squide] An environment variable for key "${key}" has been registered with the value "${value}".`);
    }

    registerVariables(variables: Partial<EnvironmentVariables>) {
        this.#environmentVariablesRegistry.addVariables(variables);

        this._runtime.logger
            .withText("[squide] The following environment variables has been registered:")
            .withObject(variables)
            .debug();
    }

    getVariable<T extends EnvironmentVariableKey>(key: T) {
        return this.#environmentVariablesRegistry.getVariable(key);
    }

    getVariables() {
        return this.#environmentVariablesRegistry.getVariables();
    }
}

export function getEnvironmentVariablesPlugin(runtime: Runtime) {
    const plugin = runtime.getPlugin(EnvironmentVariablesPluginName, {
        throwOnNotFound: false
    }) as EnvironmentVariablesPlugin;

    if (!plugin) {
        throw new Error("[squide] The getEnvironmentVariablesPlugin function is called but no EnvironmentVariablesPlugin instance has been registered with the runtime. Did you provide a EnvironmentVariablesPlugin instance to the runtime instance?");
    }

    return plugin;
}
